import { ButtonPrimary, ButtonSecondary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { Table } from "@nova/ds-component-table"
import { SavedContact } from "@nova/schemas"
import classNames from "classnames"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useMemo, useState } from "react"
import {
    useCreateSavedContact,
    useDeleteSavedContact,
    useSavedContacts,
    useUpdateSavedContact,
} from "./SavedContactsQueries"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { CreateSavedContactModal, EditSavedContactModal } from "./SavedContactsModals"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"

type SortDir = "ASC" | "DESC"
type SortField = "name" | "phone" | "email"
interface SortState {
    field: SortField
    dir: SortDir
}

export const MissionGroupsSettingsContactsTabContent = () => {
    const { isEditor } = useWorkspacePermissions()
    const { contacts, isLoading } = useSavedContacts()
    const { createSavedContact } = useCreateSavedContact()
    const [sort, setSort] = useState<SortState>({ field: "name", dir: "ASC" })

    const sortedContacts = useMemo(() => {
        const { field, dir } = sort
        const mul = dir === "ASC" ? 1 : -1
        return contacts.toSorted((a, b) => {
            const av = a[field]?.toLowerCase()
            const bv = b[field]?.toLowerCase()
            if (av == null && bv == null) return 0
            if (av == null) return 1
            if (bv == null) return -1
            if (av < bv) return -mul
            if (av > bv) return mul
            return 0
        })
    }, [contacts, sort])

    const handleSort = (field: SortField) => {
        setSort((prev) => {
            if (prev.field !== field) return { field, dir: "DESC" }
            return { field, dir: prev.dir === "DESC" ? "ASC" : "DESC" }
        })
    }
    const confirmationModal = useConfirmationModalContext()

    const handleCreateContactClick = () => {
        confirmationModal.confirm<Pick<SavedContact, "name" | "email" | "phone">>({
            RenderComponent: ({ confirm, cancel, isPerformingAsyncConfirmCallback }) => (
                <CreateSavedContactModal
                    confirm={confirm}
                    cancel={cancel}
                    isPerformingAsyncConfirmCallback={isPerformingAsyncConfirmCallback}
                />
            ),
            asyncConfirmCallback: async ({ name, email, phone }) => {
                await createSavedContact({ name, email, phone })
            },
        })
    }

    if (isLoading) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }
    if (!sortedContacts.length) {
        return (
            <div className="w-full h-full flex flex-col gap-4 justify-center items-center">
                <Icons.ExtraLargeIcon Icon={Icons.AccountCircle} className="text-white-300" />
                <div className="flex flex-col max-w-[350px] text-center">
                    <div className="text-lg">No Saved Contacts yet</div>
                    <div className="text-gray-200">
                        Save phone numbers or emails with a name to quickly add them to mission
                        groups or share live streams.
                    </div>
                </div>
                {isEditor && (
                    <ButtonPrimary
                        title="New Contact"
                        LeftIcon={Icons.Plus}
                        onClick={handleCreateContactClick}
                    />
                )}
            </div>
        )
    }
    return (
        <div className="flex flex-col gap-4 h-full w-full overflow-hidden">
            <div className="flex justify-between items-center">
                <div className="text-base">Contacts</div>
                {isEditor && (
                    <ButtonSecondary
                        title="Contact"
                        LeftIcon={Icons.PersonAdd}
                        onClick={handleCreateContactClick}
                    />
                )}
            </div>
            <div className="grow overflow-hidden">
                <Table>
                    <Table.Head>
                        <Table.Header
                            className="cursor-pointer select-none"
                            onClick={() => handleSort("name")}
                        >
                            <div className="flex items-center gap-1">
                                <div>Name</div>
                                <Table.SortIndicator
                                    sortState={sort?.field === "name" ? sort.dir : "none"}
                                />
                            </div>
                        </Table.Header>
                        <Table.Header
                            className="cursor-pointer select-none"
                            onClick={() => handleSort("phone")}
                        >
                            <div className="flex items-center gap-1">
                                <div>Phone Number</div>
                                <Table.SortIndicator
                                    sortState={sort?.field === "phone" ? sort.dir : "none"}
                                />
                            </div>
                        </Table.Header>
                        <Table.Header
                            className="cursor-pointer select-none"
                            onClick={() => handleSort("email")}
                        >
                            <div className="flex items-center gap-1">
                                <div>Email</div>
                                <Table.SortIndicator
                                    sortState={sort?.field === "email" ? sort.dir : "none"}
                                />
                            </div>
                        </Table.Header>
                        <Table.Header className="!w-0 !p-0">
                            {/* For the hover actions */}
                        </Table.Header>
                    </Table.Head>
                    <Table.Body>
                        {sortedContacts.map((contact) => {
                            return <SavedContactRow key={contact.id} contact={contact} />
                        })}
                    </Table.Body>
                </Table>
            </div>
        </div>
    )
}

const SavedContactRow = ({ contact }: { contact: SavedContact }) => {
    const dispatch = useAppDispatch()
    const { isEditor } = useWorkspacePermissions()
    const activeSavedContactContextMenu = useAppSelector(selectActiveContextMenu("SavedContact"))
    const contextMenuIsForThisItem = activeSavedContactContextMenu?.data.contact.id === contact.id
    const confirmationModal = useConfirmationModalContext()
    const { updateSavedContact } = useUpdateSavedContact()
    const { deleteSavedContact } = useDeleteSavedContact()
    const contextMenuSections = useMemo(() => {
        return [
            {
                title: "Edit",
                Icon: Icons.Edit,
                onClick: async () => {
                    await confirmationModal.confirm<SavedContact>({
                        RenderComponent: ({
                            confirm,
                            cancel,
                            isPerformingAsyncConfirmCallback,
                        }) => (
                            <EditSavedContactModal
                                contact={contact}
                                confirm={confirm}
                                cancel={cancel}
                                isPerformingAsyncConfirmCallback={isPerformingAsyncConfirmCallback}
                            />
                        ),
                        asyncConfirmCallback: async ({ id, name, email, phone }) => {
                            await updateSavedContact({
                                contactId: id,
                                name,
                                email,
                                phone,
                            })
                        },
                    })
                },
            },
            {
                title: "Delete",
                Icon: Icons.Trash,
                isDestructive: true,
                onClick: () => {
                    confirmationModal
                        .confirm({
                            RenderComponent: getGenericConfirmationModalComponent({
                                title: "Delete Contact?",
                                message: `Are you sure you want to delete ${contact.name}?`,
                            }),
                        })
                        .then((didConfirm) => {
                            if (didConfirm) {
                                deleteSavedContact(contact.id)
                                    .then(() => {
                                        dispatch(
                                            setGlobalNotificationContext({
                                                status: GlobalNotificationStatus.success,
                                                message: `${contact.name} has been deleted.`,
                                                timeoutInMs: 3000,
                                            })
                                        )
                                    })
                                    .catch(() => {
                                        dispatch(
                                            setGlobalNotificationContext({
                                                status: GlobalNotificationStatus.error,
                                                message:
                                                    "Failed to delete contact. Please try again.",
                                                timeoutInMs: 3000,
                                            })
                                        )
                                    })
                            }
                        })
                },
            },
        ]
    }, [confirmationModal, contact, deleteSavedContact, dispatch, updateSavedContact])
    return (
        <>
            <Table.Row className="group/row">
                <Table.Cell>{contact.name}</Table.Cell>
                <Table.Cell className="border-l border-l-white-200">
                    {contact.phone || "--"}
                </Table.Cell>
                <Table.Cell className="border-l border-l-white-200">
                    {contact.email || "--"}
                </Table.Cell>
                <Table.Cell className="!w-0 !p-0 relative">
                    {isEditor && (
                        <div
                            className={classNames(
                                "absolute right-0 top-0 bottom-0 p-4 bg-gray-800 flex justify-center items-center opacity-0",
                                {
                                    "group-hover/row:opacity-100": !activeSavedContactContextMenu,
                                    "!opacity-100": contextMenuIsForThisItem,
                                }
                            )}
                        >
                            <Icons.SmallIcon
                                Icon={Icons.ThreeDotsSettings}
                                className="text-gray-100 hover:text-white cursor-pointer"
                                onClick={(e) => {
                                    if (activeSavedContactContextMenu) {
                                        dispatch(setActiveContextMenu(undefined))
                                        return
                                    }
                                    const rect = (
                                        e.target as HTMLDivElement
                                    ).getBoundingClientRect()
                                    dispatch(
                                        setActiveContextMenu({
                                            type: "SavedContact",
                                            data: {
                                                contact,
                                            },
                                            position: {
                                                left: rect.left,
                                                top: rect.top,
                                            },
                                        })
                                    )
                                }}
                            />
                        </div>
                    )}
                    {contextMenuIsForThisItem && (
                        <ContextMenu
                            sections={[contextMenuSections]}
                            onBlur={() => dispatch(setActiveContextMenu(undefined))}
                            isFixedPosition={true}
                            top={activeSavedContactContextMenu.position.top}
                            left={activeSavedContactContextMenu.position.left}
                            style={{
                                transform: "translateX(-100%)",
                            }}
                            containerClassName="border border-white-200"
                        />
                    )}
                </Table.Cell>
            </Table.Row>
        </>
    )
}
